/* eslint-disable no-unused-vars */
import Layout from 'components/common/Layout';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AdminLogin } from 'views/AdminLogin';
import { useDispatch, useSelector } from 'react-redux';
import ViewsLayout from 'views/ViewsLayout';
import { UserLogin1 } from 'views/Login.user';
import { Suspense, useEffect, useLayoutEffect, useState, useMemo, useRef } from 'react';
import { Loader } from 'components';
import { authActions, ssoLogin } from 'store/auth/auth';
import { EmailValidatePage } from 'views';
import {
  UserLogin,
  UserSignup,
  WelcomePage,
  ForgotPassword,
  ResetPassword,
} from 'views/loginPages';
import {
  getIsLocalHost,
  redirectToOldPlatformLoginUrl,
  isPrefixInUrl,
  redirectToLoginPage,
} from 'utils';
import { LocalLogin, LoginPublisherList } from 'views/loginPages';
import { authAPI } from 'services/newPlatformAuth.service';
import { authGoogleClientId, authGoogleKey } from 'constants';
import ButtonsPage from 'components/common/button/ButtonsPage';
import { ChatWidgetHost } from 'components/chatWidget/ChatWidgetHost';
import { isAiAssistantEnabled } from 'utils';
import { isPublisherRole, isAdminSelector, isChatWidgetAvailableSelector } from 'store/selectors';

const userPaths = [
  { path: '/userLogin', element: <UserLogin /> },
  { path: '/signup', element: <UserSignup /> },
  { path: '/account/login', element: <UserLogin1 /> },
  { path: '/signup-success', element: <WelcomePage /> },
  { path: '/reset', element: <ResetPassword /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
];

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`;`).filter(Boolean);
  let currentCookie;
  parts.forEach((part) => {
    if (part.includes(name)) {
      const value = part.split('=')[1];
      if (value) {
        currentCookie = value;
      }
    }
  });
  //return '1737796580581';
  return currentCookie;
}

const isSomethingIsMissing = (...args) => args.some((arg) => !arg);

export function AppGoogleLogin() {
  const [switchUserAction, switchUserResult] = authAPI.useSwitchUserMutation();

  const [isLoading, setIsLoading] = useState(true);

  const accessToken = useSelector((state) => state.auth.accessToken);
  const refreshToken = useSelector((state) => state.auth.refreshToken);
  const pb_number = useSelector((state) => state.auth.pb_number);
  const authType = useSelector((state) => state.auth.authType);
  const isSamsungUser = useSelector((state) => state.auth.isSamsungUser);

  const pb_email = useSelector((state) => state.auth.pb_email);

  const isUserLogin = useSelector((state) => state.auth.isUserLogin);

  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const isSsoLoading = useSelector((state) => state.auth.isSsoLoading);
  const isPublisher = useSelector(isPublisherRole);
  const isAdmin = useSelector(isAdminSelector);
  const isWidgetAvailable = useSelector(isChatWidgetAvailableSelector);
  const dispatch = useDispatch();

  const fetchFlag = useRef(false);

  const FALLBACK_ROUTE = isPublisher ? '/manage_apps' : '/Discovery/Manage';

  const showLoader = useMemo(() => {
    if (fetchFlag.current) {
      return true;
    }
    if (switchUserResult.isLoading) {
      return true;
    }
    return false;
  }, [accessToken, refreshToken, pb_number, switchUserResult.isLoading, fetchFlag.current]);

  useLayoutEffect(() => {
    const isLoginCookie = getCookie(authGoogleKey);
    if (isPrefixInUrl() && !isLoginCookie && !showLoader) {
      dispatch(authActions.setIsLoggedInToFalse());
    }
  }, []);

  useEffect(() => {
    if (pb_email) {
      window.Intercom('update', {
        email: pb_email,
      });
    }
  }, [pb_email]);

  const putAuthDataToStore = (switchUserData) => {
    if (switchUserData) {
      const {
        access_token,
        refresh_token,
        pb_email,
        pb_username,
        pb_number,
        pb_roles,
        isSamsungUser,
      } = switchUserData;
      dispatch(
        authActions.setAuthData({
          accessToken: access_token,
          refreshToken: refresh_token,
          pb_email,
          pb_username,
          isLoggedIn: true,
          pb_number,
          pb_roles,
          isSamsungUser,
        }),
      );
      fetchFlag.current = false;
    } else {
      alert(switchUserData.toString());
    }
  };

  const fetchData = async () => {
    fetchFlag.current = true;
    try {
      const res = await switchUserAction();
      putAuthDataToStore(res.data);
    } catch (error) {
      console.error('Error during switchUserAction:', error);
    } finally {
      fetchFlag.current = false;
    }
  };

  useEffect(() => {
    const isSamsungCheck = isSamsungUser !== undefined;

    if (
      isPrefixInUrl() &&
      isSomethingIsMissing(accessToken, refreshToken, pb_number, authType, isSamsungCheck) &&
      !fetchFlag.current
    ) {
      fetchData();
    }

    if (
      isPrefixInUrl() &&
      !isSomethingIsMissing(accessToken, refreshToken, pb_number, authType, isSamsungCheck)
    ) {
      if (!switchUserResult.isSuccess && fetchFlag.current) {
        console.log(fetchFlag.current);
        alert('Something wrong with switchUserAction:');
      }
      dispatch(authActions.setIsLoggedInToTrue());
    }
  }, [accessToken, refreshToken, pb_number, authType, isSamsungUser]);

  if (switchUserResult.isError) {
    let errMessage = switchUserResult.error.data.Message || 'Error during switchUserAction';
    alert(errMessage);
    redirectToLoginPage();
    return <div>switchUser error</div>;
  }

  if (showLoader) {
    return <Loader.Global delay={0} />;
  }

  return (
    <div className='App'>
      {isLoggedIn && isAiAssistantEnabled(isAdmin, isWidgetAvailable) && <ChatWidgetHost />}
      <Layout
        main={
          <Routes>
            <Route
              path='/'
              element={
                window.SHOW_ADMIN_LOGIN_ON_ROOT ? (
                  <Navigate to='/adminLogin' replace />
                ) : (
                  <UserLogin />
                )
              }
            />
            <Route path='/email-validate' element={<EmailValidatePage />} />
            {isLoggedIn && (
              <Route path='/userLogin' element={<Navigate to={FALLBACK_ROUTE} replace />} />
            )}
            {isLoggedIn && (
              <Route
                path='/*'
                element={
                  <Suspense fallback={<Loader.Global delay={1} />}>
                    <ViewsLayout />
                  </Suspense>
                }
              />
            )}
            {!isLoggedIn && (
              <>
                <Route path='/adminLogin' element={<AdminLogin />} />

                {window.SHOW_ADMIN_LOGIN_ON_ROOT
                  ? userPaths.map(({ path }) => (
                      <Route
                        key={path}
                        path={path}
                        element={<Navigate to='/adminLogin' replace />}
                      />
                    ))
                  : userPaths.map(({ path, element }) => (
                      <Route key={path} path={path} element={element} />
                    ))}

                {isUserLogin && <Route path='*' element={<Navigate to='/' replace />} />}
              </>
            )}

            {/* <Route
                path='*'
                element={
                  <main style={{ padding: '1rem' }}>
                    <p>There&apos;s nothing here!</p>
                  </main>
                }
              /> */}
            {/* Design System text page */}
            {/* <Route path='/buttons' element={<ButtonsPage />} /> */}
          </Routes>
        }
      />
    </div>
  );
}
